import { useRef, useMemo } from "react";

import useCustomTableFilters from "hooks/useCustomTableFilters";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { Button } from "components/core/basic/Button";
import { css } from "../../../styled-system/css";

const AdDetails = ({ optData }) => {
  const TABLE_ID = "priceAdDetailsTable";
  const tableRef = useRef();

  const filterProps = useCustomTableFilters(TABLE_ID);

  const tableData = useMemo(() => {
    const sourceData = optData?.pricing_data?.filter((r) => r.num_days_at_price > 0) ?? [];
    const extendedData = sourceData?.map((row) => {
      row.total_sales = row.price * row.avg_daily_sales * row.num_days_at_price;
      row.cost_per_day = row.num_days_at_price ? row.cost / row.num_days_at_price : 0;

      const { impressions, num_days_at_price, clicks, conversions, sales, cost } = row;

      const calculatedFields = {
        impressions_per_day: num_days_at_price ? impressions / num_days_at_price : 0,
        clicks_per_day: num_days_at_price ? clicks / num_days_at_price : 0,
        conversions_per_day: num_days_at_price ? conversions / num_days_at_price : 0,
        ctr_calculated: impressions ? clicks / impressions : 0,
        cr_calculated: clicks ? conversions / clicks : 0,

        sales_per_day: sales / num_days_at_price,
        cost_per_day: num_days_at_price ? cost / num_days_at_price : 0,
        acos_calculated: sales ? row.cost / sales : 0,
        tacos_calculated: row.total_sales ? row.cost / row.total_sales : 0,
      };

      return { ...row, ...calculatedFields };
    });

    return extendedData;
  }, [optData]);

  const downloadCSV = () => {
    const data = [
      [
        "price",
        "day_at_price",
        "daily_demand",
        "impressions/day",
        "clicks/day",
        "conversions/day",
        "CTR",
        "CR",
        "sales/day",
        "cost/day",
        "ACOS",
      ],
    ];

    tableData.forEach((row) => {
      data.push([
        row.price,
        row.num_days_at_price ? row.num_days_at_price.toFixed(2) : 0,
        row.avg_daily_sales ? row.avg_daily_sales.toFixed(2) : 0,
        row.impressions_per_day ? row.impressions_per_day.toFixed(2) : 0,
        row.clicks_per_day ? row.clicks_per_day.toFixed(2) : 0,
        row.conversions_per_day ? row.conversions_per_day.toFixed(2) : 0,
        row.ctr_calculated ? row.ctr_calculated.toFixed(2) : 0,
        row.cr_calculated ? row.cr_calculated.toFixed(4) : 0,
        row.sales_per_day ? row.sales_per_day.toFixed(2) : 0,
        row.cost_per_day ? row.cost_per_day.toFixed(2) : 0,
        row.acos_calculated ? row.acos_calculated.toFixed(2) : 0,
        row.tacos_calculated ? row.tacos_calculated.toFixed(2) : 0,
      ]);
    });

    const csvContent = data.map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "advertising_effect_download.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <BulkActionsTable
        tableId={TABLE_ID}
        tableRef={tableRef}
        filterWidget={true}
        hideFilterToggleIcon={true}
        {...filterProps}
        columnSpecs={[
          {
            key: "product_price",
            compareKey: null,
            options: {
              className: "d-flex text-start align-items-center ps-3",
              metric: {
                id: "price",
                format: "currency",
                accessorId: "price",
                accessor: (d) => {
                  return d?.price;
                },
                name: "Price",
                width: "100px",
              },
            },
          },
          {
            key: "num_days_at_price",
            compareKey: null,
            options: {
              metric: {
                id: "num_days_at_price",
                format: "number",
                accessor: (d) => {
                  return d?.num_days_at_price;
                },
                name: "Days at Price",
              },
            },
          },

          {
            key: "avg_daily_sales",
            compareKey: null,
            options: {
              metric: {
                id: "avg_daily_sales",
                format: "number",
                accessor: (d) => {
                  return d?.avg_daily_sales;
                },
                name: "Daily Demand",
                decimalPlaces: 2,
              },
            },
          },
          {
            key: "impressions_per_day",
            compareKey: null,
            options: {
              metric: {
                id: "impressions_per_day",
                format: "number",
                accessor: (d) => {
                  return d?.impressions_per_day;
                },
                name: "Imp/Day",
                decimalPlaces: 0,
              },
            },
          },
          {
            key: "clicks_per_day",
            compareKey: null,
            options: {
              metric: {
                id: "clicks_per_day",
                format: "number",
                accessor: (d) => {
                  return d?.clicks_per_day;
                },
                name: "Clicks/Day",
                decimalPlaces: 0,
              },
            },
          },
          {
            key: "conversions_per_day",
            compareKey: null,
            options: {
              metric: {
                id: "conversions_per_day",
                format: "number",
                accessor: (d) => {
                  return d?.conversions_per_day;
                },
                name: "Conv/Day",
                decimalPlaces: 2,
              },
            },
          },
          {
            key: "ctr_calculated",
            compareKey: null,
            options: {
              metric: {
                id: "ctr_calculated",
                format: "percent",
                accessor: (d) => {
                  return d?.ctr_calculated;
                },
                name: "CTR",
                decimalPlaces: 2,
              },
            },
          },
          {
            key: "cr_calculated",
            compareKey: null,
            options: {
              metric: {
                id: "cr_calculated",
                format: "percent",

                accessor: (d) => {
                  return d?.cr_calculated;
                },
                name: "CR",
                decimalPlaces: 2,
              },
            },
          },
          {
            key: "sales_per_day",
            compareKey: null,
            options: {
              metric: {
                id: "sales_per_day",
                format: "currency",
                accessor: (d) => {
                  return d?.sales_per_day;
                },
                name: "Ad Sales/Day",
              },
            },
          },
          {
            key: "cost_per_day",
            compareKey: null,
            options: {
              metric: {
                id: "cost_per_day",
                format: "currency",
                accessor: (d) => {
                  return d?.cost_per_day;
                },
                name: "Cost/Day",
              },
            },
          },
          {
            key: "acos_calculated",
            compareKey: null,
            options: {
              metric: {
                id: "acos_calculated",
                format: "percent",
                accessor: (d) => {
                  return d?.acos_calculated;
                },
                name: "ACOS",
                decimalPlaces: 2,
              },
            },
          },
        ]}
        data={tableData}
        className="pricing-table"
        selectionKey="price"
        groupMetrics={false}
        isExpandable={false}
        isSelectable={false}
        hideSearchFilter={true}
        defaultPageSize={10}
        tableRowDescription={"Price"}
      />
      <Button
        onPress={() => {
          downloadCSV();
        }}
        className={css({ marginLeft: "5px" })}
      >
        {"Download CSV"}
      </Button>
    </>
  );
};

export default AdDetails;
